import * as React from 'react';
import {
  Modal,
  StackLayout,
  HeadingText,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { MoveItemsList } from './components/move-items-list.component';
import { MoveFromSection } from './components/move-from-section.component';
import { MoveToSection } from './components/move-to-section.component';
import { MoveItemsFooter } from './components/move-items-footer.component';

/**
 * Props for the MoveItemsModal component
 * @interface MoveItemsModalProps
 * @property {boolean} isOpen - Controls whether the modal is visible
 * @property {() => void} onClose - Callback function when the modal is closed
 * @property {string} moveFrom - The current location of the items being moved
 * @property {string[]} moveTargets - Array of possible target locations to move items to
 * @property {string[]} selectedItems - Array of items that are selected to be moved
 * @property {(target: string) => void} onMove - Callback function when items are moved, receives the selected target
 */
export interface MoveItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moveFrom: string;
  moveTargets?: { breadcrumb: string; targetPath: string }[];
  selectedItems: string[];
  onMove: (target: string) => void;
}

/**
 * A modal component for moving items between different locations.
 * Provides a user interface for selecting a target location and confirming the move operation.
 *
 * @component
 * @example
 * ```tsx
 * <MoveItemsModal
 *   isOpen={true}
 *   onClose={() => {}}
 *   moveFrom="Content → Pages → 1.1 → Components"
 *   moveTargets={["Content → Pages → 1.2 → Components"]}
 *   selectedItems={["Item 1", "Item 2"]}
 *   onMove={(target) => console.log(`Moving to ${target}`)}
 * />
 * ```
 */
export const MoveItemsModal: React.FC<MoveItemsModalProps> = ({
  isOpen,
  onClose,
  moveFrom,
  moveTargets = [],
  selectedItems,
  onMove,
}) => {
  const { formatMessage } = useIntl();
  const [selectedTarget, setSelectedTarget] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedTarget(null);
    }
  }, [isOpen]);

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content
        width="90vw"
        css={{
          '.GDS-modal': {
            height: '90vh',
          },
          '.GDS-modal-content': {
            paddingInline: '$three',
          },
        }}
      >
        <StackLayout
          orientation="vertical"
          spacing="$one"
          horizontalAlignment="stretch"
          css={{ height: '100%' }}
        >
          <Modal.Title>
            <HeadingText level="1" size="xl">
              {formatMessage({ id: 'MOVE_ITEMS' })}
            </HeadingText>
          </Modal.Title>
          <StackLayout
            orientation="vertical"
            spacing="$one"
            horizontalAlignment="stretch"
          >
            <MoveItemsList selectedItems={selectedItems} />
            <MoveFromSection moveFrom={moveFrom} />
            <MoveToSection
              moveTargets={moveTargets}
              selectedTarget={selectedTarget}
              onTargetSelect={setSelectedTarget}
            />
            <ParagraphText
              css={{ alignSelf: 'end', color: '$onSurfaceTextSubdued' }}
            >
              {formatMessage({
                id: 'MOVE_ARRAY_ITEMS_DESCRIPTION',
              })}
            </ParagraphText>
            <MoveItemsFooter
              onClose={onClose}
              onMove={() => selectedTarget && onMove(selectedTarget)}
              isMoveButtonDisabled={!selectedTarget}
            />
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
